'use client'

import { useEffect, useState } from 'react'
import { poster } from '../components/ProductionShared'

// A finished cut of four shots. Each clip in the cut is a finished shot, and hovering one shows
// that shot's own history: the references it used, the takes that were tried (the one that was
// picked, and the ones that were not), the edit on top, and the shot in the cut. Untouched, it
// steps through the shots on its own.

type Ref = { label: string; still?: number; pos?: string; audio?: boolean }
type Shot = {
  name: string
  still: number
  refs: Ref[]
  takes: string[] // crops of the shot's own still, as stand-ins for variations
  picked: number
  edit: string
  model: string
  w: number // share of the cut
}

const SHOTS: Shot[] = [
  {
    name: 'Shot 1 · Kitchen, morning',
    still: 29,
    refs: [
      { label: 'Lead', still: 29, pos: '80% 30%' },
      { label: 'Kitchen', still: 29, pos: '10% 50%' },
    ],
    takes: ['30% 50%', '55% 50%', '75% 50%'],
    picked: 1,
    edit: 'Upscale',
    model: 'Seedance',
    w: 3,
  },
  {
    name: 'Shot 2 · Living room',
    still: 30,
    refs: [
      { label: 'Lead', still: 29, pos: '80% 30%' },
      { label: 'Brass lamp', still: 30, pos: '70% 40%' },
    ],
    takes: ['40% 50%', '65% 50%'],
    picked: 0,
    edit: 'Color match',
    model: 'Kling',
    w: 2,
  },
  {
    name: 'Shot 3 · Dining room',
    still: 22,
    refs: [
      { label: 'Lead', still: 29, pos: '80% 30%' },
      { label: 'Supporting', still: 6, pos: '50% 30%' },
      { label: 'Clock', still: 22, pos: '50% 20%' },
    ],
    takes: ['20% 50%', '50% 50%', '85% 50%'],
    picked: 2,
    edit: 'Extend to 10s',
    model: 'Veo',
    w: 4,
  },
  {
    name: 'Shot 4 · Kitchen, night',
    still: 6,
    refs: [
      { label: 'Supporting', still: 6, pos: '50% 30%' },
      { label: 'VO take 2', audio: true },
    ],
    takes: ['35% 50%', '70% 50%'],
    picked: 1,
    edit: 'Lip sync',
    model: 'Seedance',
    w: 2,
  },
]

// Layout of the graph, in the SVG's own units.
const W = 940
const H = 390
const CY = 205 // vertical centre of the graph, below the column captions
const NW = 150
const NH = 84
const COLS = [20, 270, 520, 770]
const ys = (n: number) => Array.from({ length: n }, (_, i) => CY + (i - (n - 1) / 2) * (NH + 34))
const STEP_MS = 2600

const curve = (x1: number, y1: number, x2: number, y2: number) => {
  const mid = (x1 + x2) / 2
  return `M${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`
}

function Node({
  x,
  y,
  src,
  pos = '50% 50%',
  label,
  dim = false,
  strong = false,
  audio = false,
  tag,
  onEnter,
}: {
  x: number
  y: number
  src?: string
  pos?: string
  label: string
  dim?: boolean
  strong?: boolean
  audio?: boolean
  tag?: string
  onEnter: () => void
}) {
  const top = y - NH / 2
  return (
    <g
      opacity={dim ? 0.25 : 1}
      onMouseEnter={onEnter}
      className="cursor-pointer transition-opacity duration-200 [&:hover_rect.frame]:stroke-white"
    >
      <foreignObject x={x} y={top} width={NW} height={NH}>
        {audio ? (
          <div className="flex h-full w-full items-center gap-[2px] rounded-lg bg-[#1c2a22] px-2.5">
            {Array.from({ length: 30 }, (_, i) => (
              <span
                key={i}
                className="flex-1 rounded-full bg-[#86efac]/70"
                style={{ height: `${Math.round(20 + 60 * Math.abs(Math.sin(i * 1.3) * Math.cos(i * 0.4)))}%` }}
              />
            ))}
          </div>
        ) : (
          // eslint-disable-next-line @next/next/no-img-element -- static marketing still
          <img src={src} alt="" className="h-full w-full rounded-lg object-cover" style={{ objectPosition: pos }} />
        )}
      </foreignObject>
      <rect
        className="frame"
        x={x}
        y={top}
        width={NW}
        height={NH}
        rx="8"
        fill="none"
        stroke={strong ? '#fff' : 'rgba(255,255,255,0.18)'}
        strokeWidth={strong ? 2 : 1}
      />
      {tag && (
        <g>
          <rect x={x + 6} y={top + 6} width={tag.length * 6.2 + 12} height={17} rx="8.5" fill="#fff" />
          <text x={x + 12} y={top + 18} fontSize="10" fontWeight="600" fill="#000">
            {tag}
          </text>
        </g>
      )}
      <text x={x} y={y + NH / 2 + 18} fontSize="13" fill="rgba(255,255,255,0.65)">
        {label}
      </text>
    </g>
  )
}

// What a hovered card connects to. References feed every take; only the picked take carries on
// through the edit into the cut. Ids: r0.. references, t0.. takes, e edit, f the shot in the cut.
function traceOf(shot: Shot, id: string): Set<string> {
  const refs = shot.refs.map((_, i) => `r${i}`)
  const takes = shot.takes.map((_, i) => `t${i}`)
  const picked = `t${shot.picked}`
  if (id.startsWith('r')) return new Set([id, ...takes, 'e', 'f'])
  if (id === picked || id === 'e' || id === 'f') return new Set([...refs, picked, 'e', 'f'])
  return new Set([...refs, id])
}

// One line about the hovered card, the kind of thing the real lineage panel shows.
function detailOf(shot: Shot, id: string): string {
  if (id.startsWith('r')) {
    const r = shot.refs[Number(id.slice(1))]
    const uses = SHOTS.filter(s => s.refs.some(x => x.label === r.label)).length
    return `${r.label} · ${r.audio ? 'audio' : 'reference'} · used in ${uses} shot${uses > 1 ? 's' : ''}`
  }
  if (id.startsWith('t')) {
    const n = Number(id.slice(1))
    return n === shot.picked
      ? `Take ${n + 1} · ${shot.model} · picked by the director`
      : `Take ${n + 1} · ${shot.model} · not used, still on the canvas`
  }
  if (id === 'e') return `${shot.edit} · on take ${shot.picked + 1}`
  return `${shot.name.split(' · ')[0]} · in the cut, with every step above on record`
}

function History({
  shot,
  hovered,
  onHover,
}: {
  shot: Shot
  hovered: string | null
  onHover: (id: string | null) => void
}) {
  const refY = ys(shot.refs.length)
  const takeY = ys(shot.takes.length)
  const pickY = takeY[shot.picked]
  const src = poster(shot.still)
  const lit = hovered ? traceOf(shot, hovered) : null
  const dim = (id: string) => (lit ? !lit.has(id) : id.startsWith('t') && id !== `t${shot.picked}`)
  const edge = (a: string, b: string, base: string) =>
    lit ? (lit.has(a) && lit.has(b) ? '#fff' : 'rgba(255,255,255,0.07)') : base
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="h-auto w-full"
      role="img"
      aria-label={`How ${shot.name} was made`}
      onMouseLeave={() => onHover(null)}
    >
      {['References', 'Takes', 'Edit', 'In the cut'].map((c, i) => (
        <text key={c} x={COLS[i]} y={14} fontSize="11" letterSpacing="0.08em" fill="rgba(255,255,255,0.3)">
          {c.toUpperCase()}
        </text>
      ))}
      <g fill="none" strokeWidth="1.4" className="[&>path]:transition-[stroke] [&>path]:duration-200">
        {refY.map((ry, r) =>
          takeY.map((ty, t) => (
            <path
              key={`${r}-${t}`}
              d={curve(COLS[0] + NW, ry, COLS[1], ty)}
              stroke={edge(`r${r}`, `t${t}`, t === shot.picked ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.15)')}
            />
          )),
        )}
        <path
          d={curve(COLS[1] + NW, pickY, COLS[2], CY)}
          stroke={edge(`t${shot.picked}`, 'e', '#fff')}
          strokeWidth="1.8"
        />
        <path d={curve(COLS[2] + NW, CY, COLS[3], CY)} stroke={edge('e', 'f', '#fff')} strokeWidth="1.8" />
      </g>
      {shot.refs.map((r, i) => (
        <Node
          key={r.label}
          x={COLS[0]}
          y={refY[i]}
          src={r.still !== undefined ? poster(r.still) : undefined}
          pos={r.pos}
          audio={r.audio}
          label={r.label}
          dim={dim(`r${i}`)}
          strong={hovered === `r${i}`}
          onEnter={() => onHover(`r${i}`)}
        />
      ))}
      {shot.takes.map((pos, i) => (
        <Node
          key={pos}
          x={COLS[1]}
          y={takeY[i]}
          src={src}
          pos={pos}
          label={i === shot.picked ? `Take ${i + 1}` : `Take ${i + 1} · not used`}
          dim={dim(`t${i}`)}
          strong={i === shot.picked || hovered === `t${i}`}
          tag={i === shot.picked ? 'Picked' : undefined}
          onEnter={() => onHover(`t${i}`)}
        />
      ))}
      <Node
        x={COLS[2]}
        y={CY}
        src={src}
        pos={shot.takes[shot.picked]}
        label={shot.edit}
        dim={dim('e')}
        strong={hovered === 'e'}
        onEnter={() => onHover('e')}
      />
      <Node
        x={COLS[3]}
        y={CY}
        src={src}
        pos="50% 50%"
        label={shot.name.split(' · ')[0]}
        dim={dim('f')}
        strong
        onEnter={() => onHover('f')}
      />
    </svg>
  )
}

export function LineageTimeline() {
  const [active, setActive] = useState(0)
  const [touched, setTouched] = useState(false)
  const [node, setNode] = useState<string | null>(null)

  useEffect(() => {
    if (touched) return
    const t = setInterval(() => setActive(a => (a + 1) % SHOTS.length), STEP_MS)
    return () => clearInterval(t)
  }, [touched])

  const pick = (i: number) => {
    setTouched(true)
    setActive(i)
    setNode(null)
  }
  const hoverNode = (id: string | null) => {
    if (id) setTouched(true)
    setNode(id)
  }
  const shot = SHOTS[active]

  return (
    <div className="p-6">
      <div className="mb-4 flex items-baseline justify-between gap-6">
        <p className="text-[15px] font-medium text-white">{shot.name}</p>
        <p className="truncate text-[13px] text-white/55">
          {node ? detailOf(shot, node) : 'Hover any card to trace it'}
        </p>
      </div>
      {/* Keyed so each shot's history fades in fresh. */}
      <div key={active} className="animate-in fade-in duration-300">
        <History shot={shot} hovered={node} onHover={hoverNode} />
      </div>
      <div className="mt-4 border-t border-white/[0.08] pt-5">
        <p className="mb-3 text-[13px] text-white/45">The cut. Hover a shot to see how it was made.</p>
        <div className="flex h-16 gap-1 rounded-xl bg-black/30 p-1.5">
          {SHOTS.map((s, i) => {
            const on = i === active
            return (
              <button
                key={s.name}
                type="button"
                onMouseEnter={() => pick(i)}
                onFocus={() => pick(i)}
                onClick={() => pick(i)}
                className={`relative overflow-hidden rounded-md transition-all duration-200 ${
                  on ? 'ring-2 ring-white' : 'opacity-50 hover:opacity-80'
                }`}
                style={{ flex: s.w }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element -- static marketing still */}
                <img src={poster(s.still)} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <span className="absolute bottom-1 left-1.5 rounded bg-black/60 px-1 text-[10px] text-white">
                  {s.name.split(' · ')[0]}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
